import React, { useState, useEffect } from 'react';
import { Users, Trophy, Clock, Lock, Zap } from 'lucide-react';

const SUBJECT_IMAGES = {
  Physics: 'https://images.pexels.com/photos/6038164/pexels-photo-6038164.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  Chemistry: 'https://images.pexels.com/photos/8533140/pexels-photo-8533140.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  Biology: 'https://images.pexels.com/photos/25626515/pexels-photo-25626515.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  Mathematics: 'https://images.pexels.com/photos/22690750/pexels-photo-22690750.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'General Aptitude': 'https://images.pexels.com/photos/28583190/pexels-photo-28583190.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
};

const SUBJECT_ACCENT = {
  Physics: { bg: 'from-violet-500/10 to-indigo-500/5', border: 'hover:border-violet-400', badge: 'bg-violet-100 text-violet-700' },
  Chemistry: { bg: 'from-emerald-500/10 to-teal-500/5', border: 'hover:border-emerald-400', badge: 'bg-emerald-100 text-emerald-700' },
  Biology: { bg: 'from-cyan-500/10 to-blue-500/5', border: 'hover:border-cyan-400', badge: 'bg-cyan-100 text-cyan-700' },
  Mathematics: { bg: 'from-amber-500/10 to-orange-500/5', border: 'hover:border-amber-400', badge: 'bg-amber-100 text-amber-700' },
  'General Aptitude': { bg: 'from-fuchsia-500/10 to-pink-500/5', border: 'hover:border-fuchsia-400', badge: 'bg-fuchsia-100 text-fuchsia-700' },
};

const PoolTimer = ({ quizStartsAt }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!quizStartsAt) return;
    const update = () => {
      const diff = new Date(quizStartsAt).getTime() - Date.now();
      if (diff <= 0) { setTimeLeft('Started!'); setExpired(true); return; }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${mins}m ${secs.toString().padStart(2, '0')}s`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [quizStartsAt]);

  if (!quizStartsAt) return null;
  return (
    <span className={`font-mono font-bold text-sm ${expired ? 'text-green-600' : 'text-[#FF6B6B]'}`} data-testid="pool-timer">
      {expired ? 'Quiz Started!' : timeLeft}
    </span>
  );
};

const QuizCard = ({ pool, onJoin, userJoined }) => {
  // Display count includes simulated/real bots for a more competitive feel.
  const shownCount = Math.min(pool.max_participants, pool.display_participants ?? pool.current_participants);
  const fillPct = (shownCount / pool.max_participants) * 100;
  const isFull = pool.current_participants >= pool.max_participants;
  const quizStarted = pool.quiz_started || isFull;
  const hasTimer = pool.quiz_starts_at && pool.current_participants > 0;
  const bgImage = SUBJECT_IMAGES[pool.subject] || SUBJECT_IMAGES['General Aptitude'];
  const accent = SUBJECT_ACCENT[pool.subject] || SUBJECT_ACCENT['General Aptitude'];

  const getDiffStyle = (d) => {
    const dl = (d || '').toLowerCase();
    if (dl.includes('aiims') || dl.includes('neet')) return 'bg-purple-100 text-purple-700';
    if (dl.includes('easy')) return 'bg-green-100 text-green-700';
    if (dl.includes('medium')) return 'bg-blue-100 text-blue-700';
    return 'bg-red-100 text-red-700';
  };

  return (
    <div className={`quiz-card overflow-hidden ${accent.border}`} data-testid={`quiz-card-${pool.id}`}>
      {/* Subject Hero Image */}
      <div className="relative h-36 overflow-hidden">
        <img
          src={bgImage}
          alt={pool.subject}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        <div className={`absolute inset-0 bg-gradient-to-t ${accent.bg} from-white via-white/70 to-transparent`} />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${accent.badge} backdrop-blur-sm shadow-sm`}>
            {pool.subject}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <span className={`px-2.5 py-1 rounded-lg text-xs font-bold uppercase ${getDiffStyle(pool.difficulty)} backdrop-blur-sm shadow-sm`}>
            {pool.difficulty}
          </span>
        </div>
        {/* Prize overlay on image */}
        <div className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-xl shadow-md">
          <Trophy className="w-4 h-4 text-[#FBBF24]" />
          <span className="font-black text-[#111827] text-sm" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {pool.prize_pool?.toLocaleString()} Pts
          </span>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-5">
        <h3 className="text-lg font-bold text-[#111827] mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          {pool.topic}
        </h3>

        <div className="space-y-2.5 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#6B7280]">
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">Players</span>
            </div>
            <span className="font-bold text-[#111827]" data-testid={`participant-count-${pool.id}`}>{shownCount}/{pool.max_participants}</span>
          </div>

          {pool.max_prize_pool && pool.prize_pool < pool.max_prize_pool && (
            <div className="flex items-center justify-between text-xs text-[#9CA3AF]">
              <span>Max prize pool</span>
              <span className="font-medium">up to {pool.max_prize_pool?.toLocaleString()} pts</span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-xs text-[#9CA3AF] font-medium">Top 3% win</span>
            {hasTimer && !quizStarted ? (
              <div className="flex items-center gap-1.5">
                <Clock className="w-3 h-3 text-[#FF6B6B]" />
                <PoolTimer quizStartsAt={pool.quiz_starts_at} />
              </div>
            ) : (
              <span className="text-xs text-[#9CA3AF] font-medium">{pool.platform_fee_percent || 15}% platform fee</span>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="relative h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 transition-all duration-500 rounded-full shimmer ${isFull || quizStarted ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4]'}`}
              style={{ width: `${fillPct}%` }}
            />
          </div>
          <p className={`text-xs mt-1.5 font-medium ${quizStarted ? 'text-green-600' : 'text-[#6B7280]'}`}>
            {isFull ? 'Pool Full — Quiz Live!' : quizStarted ? 'Timer expired — Quiz Live!' : `${Math.max(0, pool.max_participants - shownCount)} spots left`}
          </p>
        </div>

        {/* Action Button */}
        {userJoined ? (
          quizStarted ? (
            <button onClick={() => onJoin(pool, 'start')}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(5,150,105)] active:translate-y-1 active:shadow-none transition-all"
              data-testid={`start-quiz-button-${pool.id}`}
            ><Zap className="w-4 h-4" />Start Quiz</button>
          ) : (
            <div className="w-full px-6 py-3 bg-slate-100 text-[#6B7280] rounded-2xl font-bold text-center flex items-center justify-center gap-2" data-testid={`waiting-button-${pool.id}`}>
              <Clock className="w-4 h-4 animate-pulse" />Waiting for pool to fill...
            </div>
          )
        ) : isFull ? (
          <div className="w-full px-6 py-3 bg-slate-200 text-[#6B7280] rounded-2xl font-bold text-center flex items-center justify-center gap-2">
            <Lock className="w-4 h-4" />Pool Full
          </div>
        ) : (
          <button onClick={() => onJoin(pool, 'join')}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#0EA5E9] to-[#0284C7] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(2,132,199)] hover:shadow-[0_6px_0_rgb(2,132,199)] hover:translate-y-[-2px] active:translate-y-1 active:shadow-none transition-all"
            data-testid={`join-quiz-button-${pool.id}`}
          >
            <span className="flex items-center justify-center gap-2">
              Enter Pool <span className="text-[#FBBF24]">&bull;</span> {pool.entry_fee} Points
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default QuizCard;
